/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/bone.tsx
   All Bone domain cards with text pre-rendered as JSX.
   Edit the `rendered` field to update display content.
   Edit the `text` field to update search index content.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  STRESS,
  HOPE,
  MELEE,
  EVASION,
  CLOSE,
  FAR,
  VERY_CLOSE,
  LONG_REST,
  SHORT_REST,
  ADVANTAGE,
  WEAPON_DAMAGE,
} from '../../templates';

const boneCards: DomainCard[] = [
  {
    name: "Deft Maneuvers",
    level: 1,
    domain: "Bone",
    type: "Ability",
    recallCost: 0,
    text: "Once per rest, **mark a Stress** to sprint anywhere within Far range without making an Agility Roll to get there.\n\nIf you end this movement within Melee range of an adversary and immediately make an attack against them, gain a +1 bonus to the attack roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, <strong className="inline-strong">mark a {STRESS}</strong> to sprint anywhere within {FAR} range without making an Agility Roll to get there.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">If you end this movement within {MELEE} range of an adversary and immediately make an attack against them, gain a +1 bonus to the attack roll.</p>
      </>
    ),
  },
  {
    name: "I See It Coming",
    level: 1,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When you're targeted by an attack made from beyond Melee range, you can **mark a Stress** to roll a **d4** and gain a bonus to your Evasion equal to the result against the attack.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you're targeted by an attack made from beyond {MELEE} range, you can <strong className="inline-strong">mark a {STRESS}</strong> to roll a <strong className="inline-strong">d4</strong> and gain a bonus to your {EVASION} equal to the result against the attack.</p>
      </>
    ),
  },
  {
    name: "Untouchable",
    level: 1,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "Gain a bonus to your Evasion equal to half your Agility.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Gain a bonus to your {EVASION} equal to half your Agility.</p>
      </>
    ),
  },
  {
    name: "Ferocity",
    level: 2,
    domain: "Bone",
    type: "Ability",
    recallCost: 2,
    text: "When you cause an adversary to mark 1 or more Hit Points, you can **spend 2 Hope** to increase your Evasion by the number of Hit Points they marked. This bonus lasts until after the next attack made against you.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you cause an adversary to mark 1 or more Hit Points, you can <strong className="inline-strong">spend 2 {HOPE}</strong> to increase your {EVASION} by the number of Hit Points they marked. This bonus lasts until after the next attack made against you.</p>
      </>
    ),
  },
  {
    name: "Strategic Approach",
    level: 2,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "After a long rest, place a number of tokens equal to your Knowledge on this card (minimum 1). The first time you move within Close range of an adversary and make an attack against them, you can spend one token to choose one of the following options:\n\n- You make the attack with advantage.\n- You clear a Stress on an ally within Melee range of the adversary.\n- You add a **d8** to your damage roll.\n\nWhen you take a long rest, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">After a {LONG_REST}, place a number of tokens equal to your Knowledge on this card (minimum 1). The first time you move within {CLOSE} range of an adversary and make an attack against them, you can spend one token to choose one of the following options:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You make the attack with {ADVANTAGE}.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You clear a {STRESS} on an ally within {MELEE} range of the adversary.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You add a <strong className="inline-strong">d8</strong> to your damage roll.</span></div>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">When you take a {LONG_REST}, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Brace",
    level: 3,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When you mark an Armor Slot to reduce incoming damage, you can **mark a Stress** to mark an additional Armor Slot.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you mark an Armor Slot to reduce incoming damage, you can <strong className="inline-strong">mark a {STRESS}</strong> to mark an additional Armor Slot.</p>
      </>
    ),
  },
  {
    name: "Tactician",
    level: 3,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When you Help an Ally, they can spend a Hope to add one of your Experiences to their roll alongside your advantage die.\n\nWhen making a Tag Team Roll, you can roll a **d20** as your Hope Die.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you Help an Ally, they can spend a Hope to add one of your Experiences to their roll alongside your advantage die.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When making a Tag Team Roll, you can roll a <strong className="inline-strong">d20</strong> as your Hope Die.</p>
      </>
    ),
  },
  {
    name: "Boost",
    level: 4,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "**Mark a Stress** to boost off a willing ally within Close range, fling yourself into the air, and perform an aerial attack against a target within Far range. You have advantage on the attack, add a **d10** to the damage roll, and end your move within Melee range of the target.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Mark a {STRESS}</strong> to boost off a willing ally within {CLOSE} range, fling yourself into the air, and perform an aerial attack against a target within {FAR} range. You have {ADVANTAGE} on the attack, add a <strong className="inline-strong">d10</strong> to the damage roll, and end your move within {MELEE} range of the target.</p>
      </>
    ),
  },
  {
    name: "Redirect",
    level: 4,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When an attack made against you from beyond Melee range fails, roll a number of **d6s** equal to your Proficiency. If any roll a 6, you can **mark a Stress** to redirect the attack to damage an adversary within Very Close range instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an attack made against you from beyond {MELEE} range fails, roll a number of <strong className="inline-strong">d6s</strong> equal to your Proficiency. If any roll a 6, you can <strong className="inline-strong">mark a {STRESS}</strong> to redirect the attack to damage an adversary within {VERY_CLOSE} range instead.</p>
      </>
    ),
  },
  {
    name: "Know Thy Enemy",
    level: 5,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When observing a creature, you can make an **Instinct Roll** against them. On a success, **spend a Hope** and ask the GM for one set of information about the target from the following options:\n\n- Their unmarked Hit Points and Stress.\n- Their Difficulty and damage thresholds.\n- Their tactics and standard attack damage dice.\n- Their features and Experiences.\n\nAdditionally on a success, you can **mark a Stress** to remove a Fear from the GM's Fear Pool.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When observing a creature, you can make an <strong className="inline-strong">Instinct Roll</strong> against them. On a success, <strong className="inline-strong">spend a Hope</strong> and ask the GM for one set of information about the target from the following options:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Their unmarked Hit Points and Stress.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Their Difficulty and damage thresholds.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Their tactics and standard attack damage dice.</span></div>
        <div key={5} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Their features and Experiences.</span></div>
        <div key={6} className="card-text-gap" />
        <p key={7} className="card-text-para">Additionally on a success, you can <strong className="inline-strong">mark a Stress</strong> to remove a Fear from the GM's Fear Pool.</p>
      </>
    ),
  },
  {
    name: "Signature Move",
    level: 5,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "Name and describe your signature combat move. Once per rest, when you perform this signature move as part of an action you're taking, you can roll a **d20** as your Hope Die. On a success, clear a Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Name and describe your signature combat move. Once per rest, when you perform this signature move as part of an action you're taking, you can roll a <strong className="inline-strong">d20</strong> as your Hope Die. On a success, clear a Stress.</p>
      </>
    ),
  },
  {
    name: "Rapid Riposte",
    level: 6,
    domain: "Bone",
    type: "Ability",
    recallCost: 0,
    text: "When an attack made against you from within Melee range fails, you can **mark a Stress** and seize the opportunity to deal the weapon damage of one of your active weapons to the attacker.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an attack made against you from within {MELEE} range fails, you can <strong className="inline-strong">mark a {STRESS}</strong> and seize the opportunity to deal the {WEAPON_DAMAGE} of one of your active weapons to the attacker.</p>
      </>
    ),
  },
  {
    name: "Recovery",
    level: 6,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "During a short rest, you can choose a long rest downtime move instead. You can **spend a Hope** to let an ally do the same.",
    rendered: (
      <>
        <p key={0} className="card-text-para">During a {SHORT_REST}, you can choose a {LONG_REST} downtime move instead. You can <strong className="inline-strong">spend a {HOPE}</strong> to let an ally do the same.</p>
      </>
    ),
  },
  {
    name: "Bone-Touched",
    level: 7,
    domain: "Bone",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Bone domain, gain the following benefits:\n\n- +1 bonus to Agility\n- Once per rest, you can **spend 3 Hope** to cause an attack that succeeded against you to fail instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your loadout are from the Bone domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>+1 bonus to Agility</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Once per rest, you can <strong className="inline-strong">spend 3 {HOPE}</strong> to cause an attack that succeeded against you to fail instead.</span></div>
      </>
    ),
  },
  {
    name: "Cruel Precision",
    level: 7,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When you make a successful attack with a weapon, gain a bonus to your damage roll equal to either your Finesse or Agility.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a successful attack with a weapon, gain a bonus to your damage roll equal to either your Finesse or Agility.</p>
      </>
    ),
  },
  {
    name: "Breaking Blow",
    level: 8,
    domain: "Bone",
    type: "Ability",
    recallCost: 3,
    text: "When you make a successful attack, you can **mark a Stress** to make the next successful attack against that same target deal an extra **2d12** damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a successful attack, you can <strong className="inline-strong">mark a {STRESS}</strong> to make the next successful attack against that same target deal an extra <strong className="inline-strong">2d12</strong> damage.</p>
      </>
    ),
  },
  {
    name: "Wrangle",
    level: 8,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "Make an Agility Roll against all targets within Close range. **Spend a Hope** to move targets you succeed against, and any willing allies within Close range, to another point within Close range.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make an Agility Roll against all targets within {CLOSE} range. <strong className="inline-strong">Spend a {HOPE}</strong> to move targets you succeed against, and any willing allies within {CLOSE} range, to another point within {CLOSE} range.</p>
      </>
    ),
  },
  {
    name: "On the Brink",
    level: 9,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "When you have 2 or fewer Hit Points unmarked, you don't take Minor damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you have 2 or fewer Hit Points unmarked, you don't take Minor damage.</p>
      </>
    ),
  },
  {
    name: "Splintering Strike",
    level: 9,
    domain: "Bone",
    type: "Ability",
    recallCost: 3,
    text: "**Spend a Hope** and make an attack against all adversaries within your weapon's range. Once per long rest, on a success against any targets, roll your weapon's damage and distribute that damage however you wish between the targets you succeeded against. Before you deal damage to each target, roll an additional damage die and add its result to the damage you deal to them.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> and make an attack against all adversaries within your weapon's range. Once per {LONG_REST}, on a success against any targets, roll your weapon's damage and distribute that damage however you wish between the targets you succeeded against. Before you deal damage to each target, roll an additional damage die and add its result to the damage you deal to them.</p>
      </>
    ),
  },
  {
    name: "Deathrun",
    level: 10,
    domain: "Bone",
    type: "Ability",
    recallCost: 1,
    text: "**Spend 3 Hope** to run a straight path through the battlefield to a point within Far range, making an attack against all adversaries within your weapon's range along that path. Choose the order in which you deal damage to the targets you succeeded against. For the first, roll your weapon damage with a +1 bonus to your Proficiency. Then remove a die from your damage roll and deal the remaining damage to the next target. Continue to remove a die for each subsequent target until you have no more damage dice or adversaries.\n\nYou can't target the same adversary more than once per attack.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend 3 {HOPE}</strong> to run a straight path through the battlefield to a point within {FAR} range, making an attack against all adversaries within your weapon's range along that path. Choose the order in which you deal damage to the targets you succeeded against. For the first, roll your weapon damage with a +1 bonus to your Proficiency. Then remove a die from your damage roll and deal the remaining damage to the next target. Continue to remove a die for each subsequent target until you have no more damage dice or adversaries.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can't target the same adversary more than once per attack.</p>
      </>
    ),
  },
  {
    name: "Swift Step",
    level: 10,
    domain: "Bone",
    type: "Ability",
    recallCost: 2,
    text: "When an attack made against you fails, clear a Stress. If you can't clear a Stress, gain a Hope.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an attack made against you fails, clear a Stress. If you can't clear a Stress, gain a Hope.</p>
      </>
    ),
  },
];

export default boneCards;
